import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';

const SearchContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid var(--input-border-color);
  border-radius: 4px;
  font-size: 1rem;
  background-color: var(--input-bg-color);
  color: var(--input-text-color);
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--button-primary-bg);
    box-shadow: 0 0 0 2px ${props => props.theme.mode === 'dark' ? 'rgba(91, 157, 255, 0.2)' : 'rgba(74, 144, 226, 0.2)'};
  }
  
  &:disabled {
    background-color: ${props => props.theme.mode === 'dark' ? '#2d2d2d' : '#f5f5f5'};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const SearchButton = styled.button`
  padding: 0.75rem 1rem;
  background-color: var(--button-primary-bg);
  color: var(--button-primary-text);
  border: none;
  margin-top: 0.5rem;
  margin-left: 0.25rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  font-weight: 500;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.mode === 'dark' ? '#4a72b7' : '#1a4288'};
  }
  
  &:disabled {
    background-color: ${props => props.theme.mode === 'dark' ? '#404040' : '#cccccc'};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.mode === 'dark' ? '#ff8080' : '#d32f2f'};
  font-size: 0.875rem;
  margin-top: 0.5rem;
  width: 100%;
  transition: color 0.3s ease;
`;

const TickerSearch = ({ onTickerSubmit, initialTicker, isLoading, error: externalError }) => {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState(externalError || '');
  
  // Initialize with initialTicker if provided
  useEffect(() => {
    if (initialTicker && !inputValue) {
      setInputValue(initialTicker);
    }
    
    // Update error message if provided from parent
    if (externalError) {
      setError(externalError);
    }
  }, [initialTicker, externalError]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) {
      setError('Please enter a ticker symbol');
      return;
    }
    
    setError('');
    onTickerSubmit(inputValue.trim().toUpperCase());
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <SearchContainer>
        <SearchInput
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.toUpperCase())}
          placeholder="Enter Ticker Symbol (e.g. AAPL)"
          aria-label="Ticker Symbol"
          disabled={isLoading}
          theme={theme}
        />
        <SearchButton 
          type="submit" 
          disabled={isLoading || !inputValue.trim()}
          theme={theme}
        >
          {isLoading ? 'Loading...' : 'Fetch'}
        </SearchButton>
      </SearchContainer>
      {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
    </form>
  );
};

export default TickerSearch;
